/**
 * Guides content for Mu Beginning (Season 6).
 * Values taken from Server/MuServer Data configs.
 * Item icons served from /guides/items/*.webp
 */

const I = (file) => `/guides/items/${file}`;

const guidesContent = [
  {
    id: "basic",
    title: "Basic information",
    subtitle: "SERVER BASICS",
    topics: [
      {
        id: "welcome",
        title: "Welcome to Mu Beginning",
        paragraphs: [
          "Mu Beginning is a Season 6 Episode 3 private server with classic MU gameplay, custom systems and quality-of-life features.",
          "Use this Guides section to learn classes, resets, VIP, dungeons, Fenrir crafting, events and more — all values match our live GameServer data.",
          "Join Discord for announcements, support and community help.",
        ],
      },
      {
        id: "game-options",
        title: "Game Options (Game Settings)",
        paragraphs: [
          "Open Game Settings from the in-game System / Escape menu (Option / Game Settings). The window title is “Game Settings”.",
          "Settings are saved locally under Data\\Custom\\GameOption\\GameOption.xml so they keep after you restart the client.",
          "There are two main tabs: Game and Graphics. Use them to control combat helpers, UI display, performance, resolution, FPS, sound and what is rendered on screen.",
          "Tip: if the game feels laggy in crowded maps or events, lower Graphics Mode, reduce FPS, or enable Hide options (players/monsters/wings) to improve FPS.",
        ],
      },
      {
        id: "game-options-game-tab",
        title: "Game Tab — Controls & Display",
        paragraphs: [
          "Drop Item / Show Name Item — choose “Always Show item Names” or “Do Not Show”. When always-show is on, ground item names stay visible without hovering each drop.",
          "Auto HP — Turn On / Turn Off. When enabled, the client requests automatic HP recovery behavior from the server (useful while hunting).",
          "Auto Attack — Turn On / Turn Off. Enables automatic attack / auto-PK style combat assist (Auto Attack). Use carefully and follow server fair-play rules.",
          "Show HP Bar — Always Show or Do Not Show. Controls whether HP bars are drawn above characters/monsters.",
          "Show Character Name — Always Show or Do Not Show. Controls whether character names stay visible on screen.",
          "VSync — locks the frame presentation to your monitor refresh rate to reduce screen tearing (may lower FPS on weak PCs).",
          "Effect 15 — toggles an extra visual effect layer used by the client (can be turned off for performance).",
          "Screen Camera — toggles the screen/camera helper mode used by our custom camera option.",
        ],
      },
      {
        id: "game-options-graphics-tab",
        title: "Graphics Tab — Performance & Sound",
        paragraphs: [
          "Mode Graphics — presets: Low, Medium, High, Ultra. Changing the preset adjusts shadows, glow, excellent effects and related quality flags automatically.",
          "FPS Limit — choose 60 / 120 / 144 / 175 / 200 / Unlimited. Higher FPS feels smoother but needs a stronger PC. Unlimited removes the soft cap.",
          "Option Game (render toggles) — Hide Wings, Hide Weapon, Hide Texture, Hide Monster, Hide Terrain, Hide Player. Use these to simplify what is drawn and raise FPS in war/events.",
          "On/Off Music — enable or disable background music. Music Volume slider (0–100) controls loudness when music is on.",
          "On/Off World Sound — enable or disable world / environment sounds (monster hits, map ambience, etc.).",
          "Resolution — pick your screen resolution from the list (scroll if needed). The client applies the new size and refreshes UI layout.",
          "Font Size — Font Size 13 / 14 / 15. Larger fonts help readability on high-resolution monitors.",
          "Recommended: start with High + 60/120 FPS; if you drop frames in Castle Siege or invasions, switch to Medium/Low and hide other players/monsters.",
        ],
      },
      {
        id: "helper",
        title: "MU Helper",
        paragraphs: [
          "The MU Helper assists with auto-hunt, potions and pick-up settings.",
          "Configure it carefully and follow fair-play rules — abuse may result in penalties.",
          "Game Options (Auto Attack / Auto HP) work together with Helper settings — review both so your character behaves as you expect.",
        ],
      },
      {
        id: "interface",
        title: "Game Interface",
        paragraphs: [
          "Use inventory, warehouse, party, quest and Event Time panels to play efficiently.",
          "Open Event Time in-game to see live countdowns for Blood Castle, invasions and more — the website Upcoming Events panel uses the same schedules.",
          "Voice chat push-to-talk is F1 (see Systems → Voice Chat). Game Settings handles graphics/sound/UI; hotkeys like F1 are separate.",
        ],
      },
    ],
  },

  {
    id: "classes",
    title: "Classes",
    subtitle: "CHARACTER BUILDS",
    topics: [
      {
        id: "dw",
        title: "Dark Wizard (DW)",
        paragraphs: [
          "Ranged magic class. Primary damage stat is Energy; Vitality for survivability.",
          "Strong in PvE waves and party content with AoE skills. Focus positioning and mana management.",
          "Progression path includes Soul Master and Grand Master evolutions (Season 6).",
        ],
      },
      {
        id: "dk",
        title: "Dark Knight (DK)",
        paragraphs: [
          "Melee front-line fighter. Strength for damage, Agility for attack/defense speed, Vitality for HP.",
          "Excellent for PvP and tanking events such as Blood Castle and Chaos Castle.",
          "Evolves through Blade Knight and Blade Master.",
        ],
      },
      {
        id: "fe",
        title: "Fairy Elf / Muse Elf (FE)",
        paragraphs: [
          "Hybrid support and ranged DPS. Agility builds for bow damage; Energy for healing/buff utility.",
          "Highly valued in parties for buffs and sustain. Strong in PvE and Castle Siege support roles.",
          "Evolves through Muse Elf and High Elf.",
        ],
      },
      {
        id: "mg",
        title: "Magic Gladiator (MG)",
        paragraphs: [
          "Hybrid warrior-mage. Can use swords and magic; often Strength/Energy hybrids.",
          "Flexible for both close combat and ranged spells. Strong all-rounder for events and farming.",
          "Does not use the same 2nd/3rd quest path as starter classes — check Quest guides in-game.",
        ],
      },
      {
        id: "dl",
        title: "Dark Lord (DL)",
        paragraphs: [
          "Command-based class with pets and party leadership skills. Leadership (Command) is a key stat.",
          "Strong in PvP and large-scale fights. Uses Dark Horse / Dark Raven and special DL sets.",
          "Requires proper Leadership investment for skill effectiveness.",
        ],
      },
      {
        id: "su",
        title: "Summoner (SU)",
        paragraphs: [
          "Magic summoner class with curses, books and pet summons. Energy is the main damage stat.",
          "Excels at mid/long range control and PvE. Learn book skills and summon management early.",
          "Evolves through Bloody Summoner and Dimension Master.",
        ],
      },
      {
        id: "rf",
        title: "Rage Fighter (RF)",
        paragraphs: [
          "Close-range martial artist with gloves and special RF skills. Strength and Vitality are core.",
          "High burst and mobility in PvP; strong farming when geared.",
          "Evolves through Fist Master. No Grow Lancer (GL) on this server.",
        ],
      },
    ],
  },

  {
    id: "systems",
    title: "Systems",
    subtitle: "SERVER FEATURES",
    topics: [
      {
        id: "battle-pass",
        title: "Battle Pass",
        paragraphs: [
          "Our Battle Pass runs in seasonal cycles of 90 days. Progress by completing daily/weekly quests and earning Battle Pass levels (up to level 50).",
          "There are two tracks: Free (available to everyone) and Premium (paid upgrade with better rewards).",
          "Premium pass costs 600 WCoin. You can also buy limited Battle Pass levels with WCoin (200 WCoin per level, max 2 purchases per day).",
          "Rewards include jewels, Kundun boxes and exclusive Premium rewards (including pets on higher Premium levels).",
          "Stay logged in and complete event/kill/jewel quests listed in the Battle Pass window to advance faster.",
        ],
      },
      {
        id: "dungeons",
        title: "Dungeon System",
        paragraphs: [
          "Mu Beginning includes a custom Dungeon catalog (NPC) with daily entries. Daily reset hour: 08:00 server time.",
          "VIP increases daily dungeon entry caps: Normal 1 / Bronze 2 / Silver 3 / Gold 4 (where applicable to VIP rules).",
          "Same-IP partying inside dungeons is blocked.",
          "Blood Castle Dungeon — 4 tiers by reset (0–15 / 16–25 / 26–35 / 36–50), ~15 min, up to 10/day. Farm: high EXP rewards.",
          "Chaos Castle Dungeon — 4 tiers, ~10 min, 10/day. Farm: Jewel of Chaos / Bless / Soul / Life / Creation / Guardian packs.",
          "Devil Square Dungeon — 4 tiers, ~20 min, 10/day. Farm: Gemstone, Jewel of Harmony, Lower Refine Stone pools.",
          "Nightmare Dungeon (Battle of Nightmare) — T1–T4, ~20 min, 10/day. Farm: EXP + Beginning Boxes.",
          "Kanturu Dungeon (Battle of Maya) — T1–T4, ~45 min, 5/day. Farm: Fenrir materials — Splinter of Armor, Bless of Guardian, Claw of Beast.",
          "Imperial Guardian — difficulties 0–3, ~55 min, 10/day. Master experience enabled on this dungeon.",
        ],
      },
      {
        id: "vip",
        title: "VIP System",
        paragraphs: [
          "Buy VIP in-game with the End key (Custom Buy VIP). Each package lasts 30 days.",
          "VIP Bronze — +10% Experience, +10% Drop. Cost: 300 Credits.",
          "VIP Silver — +20% Experience, +20% Drop. Cost: 600 Credits.",
          "VIP Gold — +30% Experience, +30% Drop. Cost: 1000 Credits.",
          "Higher VIP also improves some craft success rates (example: Fenrir Mix) and dungeon daily entry caps.",
          "Account-level EXP/Drop bonuses from VIP tiers stack with server rates according to GameServer settings.",
        ],
      },
      {
        id: "fenrir-mix",
        title: "Fenrir Mix",
        paragraphs: [
          "Craft Fenrir step by step using Chaos Machine / Fenrir Mix. Success rates rise with VIP (Normal / Bronze / Silver / Gold).",
          "Step 1 — Fragment of Horn: Bless×10, Soul×10, Chaos×10, Creation×10, Life×10, Splinter of Armor×20, Bless of Guardian×20 + 100,000,000 Zen. Success: 70% / 73% / 76% / 80%.",
          "Step 2 — Broken Horn: jewels×20 each (Bless/Soul/Chaos/Creation/Life), Claw of Beast×20, Fragment of Horn×5 + 500,000,000 Zen. Success: 50% / 55% / 60% / 65%.",
          "Step 3 — Horn of Fenrir (Red): jewels×30 each + Broken Horn×1 + 1,000,000,000 Zen. Success: 30% / 35% / 40% / 45%.",
          "Upgrade — Blue Fenrir: Red Fenrir + Soul×90 + Bless×90 + Chaos×60 + Life×60 + 10,000,000 Zen. Success: 70% / 75% / 80% / 85%.",
          "Upgrade — Black Fenrir: Blue Fenrir + Soul×90 + Bless×90 + Chaos×60 + Creation×60 + 10,000,000 Zen. Success: 70% / 75% / 80% / 85%.",
          "Upgrade — Gold Fenrir: Black Fenrir + Blue Fenrir. Success: 60% / 70% / 80% / 90%.",
          "Jewel packs (10/20/30) can substitute stacked jewels. Farm Splinter / Bless of Guardian / Claw mainly from Kanturu Dungeon (Battle of Maya).",
        ],
        items: [
          { src: I("splinter-of-armor.webp"), label: "Splinter of Armor" },
          { src: I("bless-of-guardian.webp"), label: "Bless of Guardian" },
          { src: I("claw-of-beast.webp"), label: "Claw of Beast" },
          { src: I("fragment-of-horn.webp"), label: "Fragment of Horn" },
          { src: I("broken-horn.webp"), label: "Broken Horn" },
          { src: I("horn-of-fenrir.webp"), label: "Horn of Fenrir" },
          { src: I("jewel-of-bless.webp"), label: "Jewel of Bless" },
          { src: I("jewel-of-soul.webp"), label: "Jewel of Soul" },
          { src: I("jewel-of-chaos.webp"), label: "Jewel of Chaos" },
          { src: I("jewel-of-life.webp"), label: "Jewel of Life" },
          { src: I("jewel-of-creation.webp"), label: "Jewel of Creation" },
        ],
      },
      {
        id: "reset",
        title: "Reset System",
        paragraphs: [
          "Command: /reset (also auto-reset is enabled). Required level: 400. After reset you return to level 1 in Lorencia.",
          "Maximum resets before Grand Reset: 50.",
          "Points reward by reset count (same table for all VIP): Reset 1–4 = 500 pts · Reset 5 = 1000 · Reset 10 = 1200 · Reset 15 = 1400 · Reset 20 = 1600 · Reset 25 = 1800 · Reset 30 = 2000 · Reset 35 = 2200 · Reset 40 = 2400 · Reset 45 = 2600 · Reset 50 = 2800.",
          "Zen cost scales with reset number (from a few million early up to ~1.5B at reset 50 before Grand Reset). After a Grand Reset, zen costs roughly double (capped at 2,000,000,000).",
          "Stat points from resets are added according to the Reset Table — plan your build before spending.",
        ],
      },
      {
        id: "grand-reset",
        title: "Grand Reset",
        paragraphs: [
          "Command: /greset (Master Reset).",
          "Requirement: 50 resets completed.",
          "Cost: 2,000,000,000 Zen.",
          "Reward: 15,000 reset points. After Grand Reset, your reset count returns to 0 and you can climb the reset ladder again.",
          "Server limit: 1 Grand Reset (Master Reset) maximum.",
        ],
      },
      {
        id: "voice",
        title: "Voice Chat",
        paragraphs: [
          "Proximity voice chat is enabled on Mu Beginning — talk with nearby players using your microphone.",
          "Push-to-talk key: F1 (hold to speak).",
          "Voice radius: 50. Available for Normal, VIP and GM accounts.",
          "Use a working mic/headset in Windows, then hold F1 in-game while near other players.",
        ],
      },
      {
        id: "quests",
        title: "Quest Systems",
        paragraphs: [
          "Several quest systems run in parallel on our server:",
          "Quest System (Daily) — kill/farm quests (example targets: spiders, goblins and higher maps). Open the Quest System UI for current objectives and rewards.",
          "Quest NPC — custom NPC class “Quest” (ID 600) placed on multiple maps for kill/collect style custom NPC quests.",
          "Quest World / Gens — Shadow Phantom Soldier and Gens steward NPCs handle world/gens-style progression quests.",
          "Classic class quests — Sebina the Priestess, Marlon and Apostle Devin for 2nd/3rd class (where applicable to your character).",
          "Elf Soldier / classic soldier quests appear in the standard MU quest chain where your class requires them — follow NPC dialogue in-game.",
          "Battle Pass also adds daily/weekly quests tied to events, kills and jewel pickups.",
        ],
      },
      {
        id: "market",
        title: "Market System",
        paragraphs: [
          "The Market is the player trading system — list items for sale so others can buy them while you are offline.",
          "You choose the listing duration in days. Maximum listing time: 30 days.",
          "Seller taxes apply by item type (approximately 5–15% depending on category).",
          "Expired listings can be kept for the seller but hidden from buyers according to Market settings — reclaim your items if they do not sell.",
        ],
      },
      {
        id: "expanded-inventory",
        title: "Expanded Inventory",
        paragraphs: [
          "Expanded Inventory unlocks extra inventory space using the Magic Backpack item.",
          "Item: Magic Backpack (section 14, index 162).",
          "Duration: 30 days per unlock item use.",
          "Use it when your main inventory is full during long farm sessions or event looting.",
        ],
      },
      {
        id: "online-bonus",
        title: "Online Bonus",
        paragraphs: [
          "Stay online to receive automatic rewards.",
          "Interval: every 60 minutes of online time.",
          "Reward: 1 Credit per interval.",
          "Condition: remain connected on the GameServer; the timer advances while your character is online. Disconnecting pauses progress toward the next payout.",
        ],
      },
    ],
  },

  {
    id: "leveling",
    title: "Leveling & Progression",
    subtitle: "GROW FASTER",
    topics: [
      {
        id: "spots",
        title: "Training Spots",
        paragraphs: [
          "Start in Lorencia / Noria / Devias starter maps, then move to higher maps as level and gear improve.",
          "Use party hunting, Online Bonus, VIP EXP and dungeons to speed early resets.",
          "Kanturu Dungeon is important later for Fenrir materials; Chaos/Devil Square dungeons help jewel income.",
        ],
      },
      {
        id: "progression",
        title: "Recommended Progression",
        paragraphs: [
          "1) Reach level 400 and start resets · 2) Unlock VIP if you play daily · 3) Use dungeons for EXP/jewels · 4) Climb to 50 resets · 5) Grand Reset for a large point boost · 6) Craft Fenrir when materials are ready.",
          "Check Commands and Event Time regularly so you never miss free reward windows.",
        ],
      },
    ],
  },

  {
    id: "events",
    title: "Events",
    subtitle: "TIMED CONTENT",
    topics: [
      {
        id: "blood-castle",
        title: "Blood Castle",
        paragraphs: [
          "Enter during the open window, clear monsters and complete the castle objective for rewards.",
          "Also available as a daily Dungeon tier instance with large EXP payouts.",
        ],
      },
      {
        id: "devil-square",
        title: "Devil Square",
        paragraphs: [
          "Survive monster waves inside Devil Square for experience and loot.",
          "Dungeon version focuses on jewel/refine material rewards.",
        ],
      },
      {
        id: "chaos-castle",
        title: "Chaos Castle",
        paragraphs: [
          "PvE survival event inside Chaos Castle. Clear monsters and stay alive for jewel rewards.",
          "Dungeon tiers scale by your reset range.",
        ],
      },
      {
        id: "kanturu",
        title: "Kanturu Event",
        paragraphs: [
          "Kanturu event content and related Maya/Kanturu dungeon battles.",
          "Kanturu Dungeon is a primary farm for Fenrir craft materials.",
        ],
      },
      {
        id: "kalima",
        title: "Kalima Kundun",
        paragraphs: [
          "Kalima Lord Kundun event. Prepare potions and a solid party for the boss window.",
        ],
      },
      {
        id: "imperial",
        title: "Imperial Guardian",
        paragraphs: [
          "Timed Imperial Guardian runs with multiple difficulties. Expect long runs (~55 min in dungeon form) and Master EXP on our dungeon settings.",
        ],
      },
      {
        id: "illusion",
        title: "Illusion Temple",
        paragraphs: [
          "Team event with relic objectives. Join when Event Time shows Illusion Temple open.",
        ],
      },
      {
        id: "invasions",
        title: "World Invasions",
        paragraphs: [
          "Scheduled invasions spawn bosses across maps. Check Event Time for the next spawn.",
          "Includes: Skeleton King, Red Dragon, Golden Invasion, White Wizard, Pouch of Blessing, White Rabbit, Fire Flame Ghost, Medusa, Ferea, Nix, God of Darkness, Core Magriffy, Lord Silvestre, Protector of Acheron, Evomon, Chicken, Horse, Goat, Monkey, Goblin, Silent Invasion, Golden Hell Maine and more.",
        ],
      },
      {
        id: "boss-together",
        title: "Boss Battle Together",
        paragraphs: [
          "Cooperative boss events (I / II / III). Gather a party and move to the event gate when the timer reaches Appeared / open.",
        ],
      },
      {
        id: "arka-war",
        title: "Arka War",
        paragraphs: [
          "Guild vs guild Arka War event. Coordinate with your guild for registration and fight windows.",
        ],
      },
      {
        id: "other-events",
        title: "Other Events",
        paragraphs: [
          "Also scheduled: Invocation Demons, Merchant Zyro, Moss Merchant, TvT, Online Lottery, Custom Quiz, Custom Bonus and Custom Drop.",
          "Open the in-game Event Time window or the website Events page for live countdowns.",
        ],
      },
    ],
  },
];

export default guidesContent;
